import { InfiniteData, QueryClient, useQueryClient } from '@tanstack/react-query';

import amplitude from '@/amplitude';
import { QUOTA_ERROR_MESSAGE, resolveCreateErrorMessage } from '@/shared/hooks/analysis/createErrorMessage';
import {
  AnalysisType as BackendAnalysisType,
  GetCurrentUserAnalysisQuotasQuery,
  GetUserAnalysesPaginatedQuery,
  SearchCriteriaInput,
  VestiaireSearchCriteriaInput,
  useCreateAnalysisFromFiltersMutation,
  useCreateVestiaireAnalysisMutation,
  useDeleteAnalysisMutation,
  useGetCurrentUserAnalysisQuotasQuery,
  useParseVintedCatalogUrlQuery,
  useUpdateAnalysisNameMutation,
} from '@/shared/hooks/queries/generated';
import i18n from '@/shared/i18n';
import type { AnalysisType } from '@/shared/types/analysis';

/**
 * Constants
 */
const GetAnalysisByIdQueryId = 'GetAnalysisById';
const GetUserAnalysesPaginatedQueryId = 'GetUserAnalysesPaginated';
const GetUserAnalysesPaginatedQueryTypes = ['catalog', 'profile'] as const;

/**
 * Hook pour gérer le renommage d'analyse avec optimistic update
 */
export const useRenameAnalysis = () => {
  const queryClient = useQueryClient();
  const updateNameMutation = useUpdateAnalysisNameMutation();

  const renameAnalysis = async (analysisId: string, newName: string) => {
    const trimmedName = newName.trim();

    // Optimistic update pour GetAnalysisById
    queryClient.setQueryData([GetAnalysisByIdQueryId, { id: analysisId }], (oldData: any) => {
      if (oldData?.getAnalysisById) {
        return {
          ...oldData,
          getAnalysisById: {
            ...oldData.getAnalysisById,
            name: trimmedName,
          },
        };
      }
      return oldData;
    });

    // Optimistic update pour GetUserAnalysesPaginated
    for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
      queryClient.setQueriesData<InfiniteData<GetUserAnalysesPaginatedQuery>>(
        {
          queryKey: [GetUserAnalysesPaginatedQueryId, queryType],
        },
        (oldData) => {
          if (oldData) {
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                getUserAnalysesPaginated: {
                  ...page.getUserAnalysesPaginated,
                  analyses: page.getUserAnalysesPaginated.analyses.map((analysis) =>
                    analysis.id === analysisId ? { ...analysis, name: trimmedName } : analysis,
                  ),
                },
              })),
            };
          }
          return oldData;
        },
      );
    }

    try {
      await updateNameMutation.mutateAsync({
        input: {
          id: analysisId,
          name: trimmedName,
        },
      });
    } catch (error) {
      // En cas d'erreur, restaurer les données originales
      queryClient.invalidateQueries({ queryKey: [GetAnalysisByIdQueryId, { id: analysisId }] });
      for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
        queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
      }

      console.error('Erreur lors du renommage:', error);
      throw error;
    }
  };

  return {
    renameAnalysis,
    isRenaming: updateNameMutation.isPending,
  };
};

/**
 * Suppression d'analyse v2 : un seul appel (soft-delete), plus de séquence
 * deactivate → remove ni de notion de statut.
 */
export const useDeleteAnalysis = () => {
  const queryClient = useQueryClient();
  const deleteMutation = useDeleteAnalysisMutation();

  const deleteAnalysis = async (analysisId: string) => {
    await deleteMutation.mutateAsync({ id: analysisId });

    // L'analyse disparaît de toutes les lectures : purge des caches
    queryClient.removeQueries({ queryKey: [GetAnalysisByIdQueryId, { id: analysisId }] });
    for (const queryType of GetUserAnalysesPaginatedQueryTypes) {
      queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
    }
  };

  return {
    deleteAnalysis,
    isDeleting: deleteMutation.isPending,
  };
};

interface CreateAnalysisData {
  title: string;
  url: string;
  type: AnalysisType;
}

const optionIds = (options: { id: number }[] | null | undefined): number[] | undefined =>
  options && options.length > 0 ? options.map((option) => option.id) : undefined;

export { QUOTA_ERROR_MESSAGE };

const createErrorMessage = (error: unknown): string => resolveCreateErrorMessage(error, (key) => i18n.t(key));

const QUOTAS_QUERY_KEY = useGetCurrentUserAnalysisQuotasQuery.getKey();

/**
 * Pré-check de quota depuis le cache serveur (quotas mensuels 2026-08-17).
 * Sans donnée en cache on laisse passer : le serveur reste le juge de paix
 * et son refus est traduit par `createErrorMessage`.
 */
const canCreateFromServerQuotas = (queryClient: QueryClient, kind: 'product' | 'vendor'): boolean => {
  const cached = queryClient.getQueryData<GetCurrentUserAnalysisQuotasQuery>(QUOTAS_QUERY_KEY);
  if (!cached) return true;
  const quotas = cached.getCurrentUserAnalysisQuotas;
  return kind === 'vendor' ? quotas.canCreateProfileAnalysis : quotas.canCreateCatalogAnalysis;
};

/**
 * Hook de création d'analyse (v2 : createAnalysisFromFilters).
 *
 * TRANSITION : les appelants fournissent encore une URL Vinted. On la parse
 * côté serveur (`parseVintedCatalogUrl`) pour obtenir les critères structurés
 * (ids), puis on crée avec `criteria` (catalog) ou `sellerVintedId` (profil).
 * Les flux natifs par critères (search builder serveur) remplaceront l'URL
 * progressivement — la signature de ce hook ne changera pas pour ses appelants.
 */
export const useCreateAnalysis = () => {
  const queryClient = useQueryClient();
  const createMutation = useCreateAnalysisFromFiltersMutation();

  const createAnalysis = async (data: CreateAnalysisData) => {
    let analysisType: BackendAnalysisType;
    let queryType: (typeof GetUserAnalysesPaginatedQueryTypes)[number];
    switch (data.type) {
      case 'product':
        analysisType = 'catalog';
        queryType = 'catalog';
        break;
      case 'vendor':
        analysisType = 'profile';
        queryType = 'profile';
        break;
      default:
        throw new Error(i18n.t('analysisMenu.unsupportedType', { type: data.type }));
    }

    // Quota mensuel SERVEUR : pré-check sur le cache, débit atomique côté back
    const creditKind = data.type === 'vendor' ? ('vendor' as const) : ('product' as const);
    if (!canCreateFromServerQuotas(queryClient, creditKind)) {
      return {
        success: false,
        error: QUOTA_ERROR_MESSAGE,
      };
    }

    try {
      let input: { name: string; criteria?: SearchCriteriaInput; sellerVintedId?: string };

      if (analysisType === 'profile') {
        // URL de profil (ou pseudo) → id membre Vinted
        const memberId = /\/member\/(\d+)/.exec(data.url)?.[1];
        input = { name: data.title, sellerVintedId: memberId ?? data.url };
      } else {
        // URL catalogue → critères structurés via le parseur serveur
        const parsed = await useParseVintedCatalogUrlQuery.fetcher({ catalogUrl: data.url })();
        const p = parsed.parseVintedCatalogUrl;

        if (p.sellerVintedId) {
          input = { name: data.title, sellerVintedId: p.sellerVintedId };
        } else {
          const criteria: SearchCriteriaInput = {
            priceMin: p.priceMin ?? undefined,
            priceMax: p.priceMax ?? undefined,
            catalogIds: optionIds(p.catalogs),
            brandIds: optionIds(p.brands),
            conditionIds: optionIds(p.conditions),
            colorIds: optionIds(p.colors),
            sizeIds: optionIds(p.sizes),
            materialIds: optionIds(p.materials),
            patternIds: optionIds(p.patterns),
            brandCollectionIds: optionIds(p.brandCollections),
            videoGamePlatformIds: optionIds(p.videoGamePlatforms),
            videoGameRatingIds: optionIds(p.videoGameRatings),
            internalMemoryCapacityIds: optionIds(p.internalMemoryCapacities),
            languageBookIds: optionIds(p.languageBooks),
            simLockIds: optionIds(p.simLocks),
            keywords: p.keywords && p.keywords.length > 0 ? p.keywords : undefined,
          };
          input = { name: data.title, criteria };
        }
      }

      const result = await createMutation.mutateAsync({ input });

      // Envoi d'un événement à Amplitude
      if (data.type === 'product') {
        amplitude.track('Produits Add Completed');
      } else if (data.type === 'vendor') {
        amplitude.track('Vendeurs Add Completed');
      }

      return {
        success: true,
        analysis: result.createAnalysisFromFilters,
      };
    } catch (error) {
      console.error("Erreur lors de la création de l'analyse:", error);

      // Envoyer un événement d'erreur à Amplitude
      if (data.type === 'product') {
        amplitude.track('Produits Add Failed', { error: (error as Error).message });
      } else if (data.type === 'vendor') {
        amplitude.track('Vendeurs Add Failed', { error: (error as Error).message });
      }

      return {
        success: false,
        error: createErrorMessage(error),
      };
    } finally {
      // Invalider les queries pour rafraîchir les données (dont le quota mensuel)
      await queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, queryType] });
      await queryClient.invalidateQueries({ queryKey: QUOTAS_QUERY_KEY });
    }
  };

  return {
    createAnalysis,
    isCreating: createMutation.isPending,
    error: createMutation.error ? i18n.t('analysisMenu.createError') : null,
  };
};

/**
 * Création directe par critères structurés (search builder, relances « zoom »
 * depuis une niche ou un article — les critères sont déjà en ids).
 * `parentAnalysisId` enregistre la lignée à la création même (l'API ne
 * recopie aucun critère : elle ne stocke que le lien).
 */
export const useCreateAnalysisFromCriteria = () => {
  const queryClient = useQueryClient();
  const createMutation = useCreateAnalysisFromFiltersMutation();

  const createAnalysisFromCriteria = async ({
    title,
    criteria,
    parentAnalysisId,
  }: {
    title: string;
    criteria: SearchCriteriaInput;
    parentAnalysisId?: string;
  }) => {
    // Quota mensuel SERVEUR : pré-check sur le cache, débit atomique côté back
    if (!canCreateFromServerQuotas(queryClient, 'product')) {
      return {
        success: false as const,
        error: QUOTA_ERROR_MESSAGE,
      };
    }
    try {
      const result = await createMutation.mutateAsync({ input: { name: title, criteria, parentAnalysisId } });
      amplitude.track('Produits Add Completed');
      return { success: true as const, analysis: result.createAnalysisFromFilters };
    } catch (error) {
      console.error("Erreur lors de la création de l'analyse:", error);
      amplitude.track('Produits Add Failed', { error: (error as Error).message });
      return {
        success: false as const,
        error: createErrorMessage(error),
      };
    } finally {
      await queryClient.invalidateQueries({ queryKey: [GetUserAnalysesPaginatedQueryId, 'catalog'] });
      await queryClient.invalidateQueries({ queryKey: QUOTAS_QUERY_KEY });
    }
  };

  return { createAnalysisFromCriteria, isCreating: createMutation.isPending };
};

/**
 * Création d'une analyse Vestiaire Collective : `criteria` (catalogue) OU `seller` (id du
 * vendeur rendu par parseVestiaireSellerRef — jamais un pseudo), jamais les deux.
 * Quota PARTAGÉ avec Vinted (catalogue / profil). `parentAnalysisId` doit désigner une
 * analyse Vestiaire (400 sinon).
 */
export const useCreateVestiaireAnalysis = () => {
  const queryClient = useQueryClient();
  const createMutation = useCreateVestiaireAnalysisMutation();

  const createVestiaireAnalysis = async ({
    title,
    criteria,
    seller,
    parentAnalysisId,
  }: {
    title: string;
    criteria?: VestiaireSearchCriteriaInput;
    seller?: string;
    parentAnalysisId?: string;
  }) => {
    const isProfile = seller != null;
    if (!canCreateFromServerQuotas(queryClient, isProfile ? 'vendor' : 'product')) {
      return { success: false as const, error: QUOTA_ERROR_MESSAGE };
    }
    const event = isProfile ? 'Vendeurs' : 'Produits';
    try {
      const result = await createMutation.mutateAsync({
        input: { name: title, criteria: isProfile ? undefined : criteria, seller, parentAnalysisId },
      });
      amplitude.track(`${event} Add Completed`, { platform: 'vestiaire' });
      return { success: true as const, analysis: result.createVestiaireAnalysis };
    } catch (error) {
      console.error("Erreur lors de la création de l'analyse Vestiaire:", error);
      amplitude.track(`${event} Add Failed`, { platform: 'vestiaire', error: (error as Error).message });
      return { success: false as const, error: createErrorMessage(error) };
    } finally {
      await queryClient.invalidateQueries({
        queryKey: [GetUserAnalysesPaginatedQueryId, isProfile ? 'profile' : 'catalog'],
      });
      await queryClient.invalidateQueries({ queryKey: QUOTAS_QUERY_KEY });
    }
  };

  return { createVestiaireAnalysis, isCreating: createMutation.isPending };
};
